/**
 * WebhookDeliveriesModal - Recent delivery attempts for a webhook
 *
 * Read-only view of the last N delivery attempts: event type, outcome,
 * HTTP status, latency, attempt count, and any error.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Badge, Button, Spinner } from '@/components/ui';
import { RefreshCw, Inbox } from 'lucide-react';
import {
  getWebhookDeliveries,
  WebhookListItem,
  WebhookDelivery,
} from '@/services/webhookService';
import { useUserTimezone } from '@/hooks/useDateFormat';

interface WebhookDeliveriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  webhook: WebhookListItem | null;
}

function formatTimestamp(date: Date | string | undefined, timeZone: string): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
  });
}

const STATUS_CLASS: Record<WebhookDelivery['status'], string> = {
  success: 'bg-status-success-bg text-status-success',
  failed: 'bg-status-error-bg text-status-error',
  retrying: 'bg-status-warning-bg text-status-warning',
  pending: 'bg-muted text-muted-foreground',
};

export function WebhookDeliveriesModal({
  isOpen,
  onClose,
  organizationId,
  webhook,
}: WebhookDeliveriesModalProps) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timeZone = useUserTimezone();

  const fetchDeliveries = useCallback(async () => {
    if (!webhook?.id || !organizationId) return;
    setLoading(true);
    setError('');
    try {
      const response = await getWebhookDeliveries(webhook.id, organizationId, 50);
      setDeliveries(response.deliveries || []);
    } catch (err: any) {
      console.error('Error fetching webhook deliveries:', err);
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [webhook?.id, organizationId]);

  useEffect(() => {
    if (isOpen) fetchDeliveries();
  }, [isOpen, fetchDeliveries]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Recent Deliveries"
      description={webhook ? `Last delivery attempts for "${webhook.name}"` : undefined}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={fetchDeliveries} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-status-error text-sm">{error}</div>
      ) : deliveries.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/30">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No deliveries yet</p>
          <p className="text-sm text-muted-foreground/70">
            Attempts will appear here once events fire for this webhook.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Event</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">HTTP</th>
                <th className="py-2 pr-4 font-medium">Latency</th>
                <th className="py-2 pr-4 font-medium">Attempts</th>
                <th className="py-2 pr-4 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4 font-mono text-xs text-foreground">{d.eventType}</td>
                    <td className="py-2 pr-4">
                      <Badge className={STATUS_CLASS[d.status]}>{d.status}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-foreground">{d.responseStatus ?? '—'}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {d.responseTime != null ? `${d.responseTime} ms` : '—'}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {d.attempts}/{d.maxAttempts}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(d.deliveredAt || d.lastAttemptAt || d.createdAt, timeZone)}
                    </td>
                  </tr>
                  {d.error && (
                    <tr className="border-b border-border/50">
                      <td colSpan={6} className="pb-2 pl-0 pr-4">
                        <div className="text-xs text-status-error bg-status-error-bg/30 rounded px-2 py-1 break-all">
                          {d.error}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default WebhookDeliveriesModal;
